import { useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";

// ─── Merit score badge ────────────────────────────────────────────────────────
function MeritBadge({ score }) {
  if (score === null || score === undefined) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
        style={{ background: "rgba(148,163,184,0.15)", color: "#94a3b8", border: "1px solid rgba(148,163,184,0.25)" }}
        data-testid="merit-badge-pending"
      >
        Pending
      </span>
    );
  }
  let bg, color, border;
  if (score >= 80) {
    bg = "rgba(16,185,129,0.12)"; color = "#10b981"; border = "1px solid rgba(16,185,129,0.25)";
  } else if (score >= 60) {
    bg = "rgba(249,115,22,0.12)"; color = "#f97316"; border = "1px solid rgba(249,115,22,0.25)";
  } else {
    bg = "rgba(239,68,68,0.12)"; color = "#ef4444"; border = "1px solid rgba(239,68,68,0.25)";
  }
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: bg, color, border }}
      data-testid="merit-badge-score"
    >
      {score}
    </span>
  );
}

// ─── KanbanCard (draggable) ───────────────────────────────────────────────────
function KanbanCard({ application, onCardClick, isDark, t }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: application.id,
    data: { application },
  });

  const student = application.student || {};
  const name = student.name || "Unknown";
  const initial = name[0]?.toUpperCase() || "?";
  const college = student.college?.replace("BITS Pilani - ", "") || "—";
  const topSkill = (student.extracted_skills || [])[0] || null;
  const verifiedCount = (student.verified_skills || []).filter(
    (v) => v.status === "verified"
  ).length;

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`kanban-card-${application.id}`}
      onClick={() => onCardClick(application)}
      className="rounded-xl cursor-grab active:cursor-grabbing transition-all duration-150 select-none"
      style={{
        background: t.cardBg,
        border: `1px solid ${t.border}`,
        padding: "12px 14px",
        marginBottom: "8px",
        opacity: isDragging ? 0.5 : 1,
        boxShadow: isDragging
          ? "0 8px 32px rgba(0,0,0,0.18)"
          : "0 1px 4px rgba(0,0,0,0.06)",
        transform: isDragging ? "rotate(2deg)" : "none",
      }}
    >
      {/* Header row: avatar + name + merit badge */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-sm"
            style={{ background: t.accentLight, color: t.accent }}
          >
            {initial}
          </div>
          <div className="min-w-0">
            <p
              className="text-sm font-semibold truncate"
              style={{ color: t.textPrimary }}
            >
              {name}
            </p>
            <p
              className="text-xs truncate"
              style={{ color: t.textMuted }}
            >
              {college}
            </p>
          </div>
        </div>
        <MeritBadge score={application.merit_score} />
      </div>

      {/* Top skill tag */}
      {topSkill && (
        <div className="mb-1.5">
          <span
            className="inline-block px-2 py-0.5 text-xs rounded-full"
            style={{
              background: isDark ? "rgba(249,115,22,0.12)" : "rgba(249,115,22,0.08)",
              color: t.accent,
              border: `1px solid rgba(249,115,22,0.2)`,
            }}
          >
            {topSkill}
          </span>
        </div>
      )}

      {/* Verified skills badge */}
      {verifiedCount > 0 && (
        <div>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full"
            style={{
              background: "rgba(16,185,129,0.12)",
              color: "#10b981",
              border: "1px solid rgba(16,185,129,0.25)",
            }}
          >
            ✓ {verifiedCount} verified
          </span>
        </div>
      )}
    </div>
  );
}

// ─── KanbanColumn (droppable) ─────────────────────────────────────────────────
function KanbanColumn({ stage, applications, onCardClick, onStageChange, isDark, t }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });

  return (
    <div
      className="flex flex-col flex-shrink-0 rounded-2xl"
      style={{
        minWidth: "260px",
        width: "260px",
        background: isOver
          ? isDark
            ? "rgba(249,115,22,0.07)"
            : "rgba(249,115,22,0.05)"
          : isDark
          ? "rgba(255,255,255,0.03)"
          : "rgba(0,0,0,0.025)",
        border: isOver
          ? `1.5px solid rgba(249,115,22,0.4)`
          : `1.5px solid ${t.border}`,
        transition: "background 0.15s, border-color 0.15s",
      }}
      data-testid={`kanban-column-${stage}`}
    >
      {/* Column header */}
      <div
        className="flex items-center justify-between px-4 py-3 rounded-t-2xl"
        style={{
          borderBottom: `1px solid ${t.border}`,
          background: isDark ? "rgba(255,255,255,0.03)" : "rgba(26,31,94,0.04)",
        }}
      >
        <span
          className="text-sm font-semibold"
          style={{ color: t.textPrimary }}
        >
          {stage}
        </span>
        <span
          className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full"
          style={{
            background: applications.length > 0 ? t.accentLight : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
            color: applications.length > 0 ? t.accent : t.textMuted,
          }}
          data-testid={`kanban-column-count-${stage}`}
        >
          {applications.length}
        </span>
      </div>

      {/* Card list — droppable target */}
      <div
        ref={setNodeRef}
        className="flex-1 overflow-y-auto p-3"
        style={{ minHeight: "120px", maxHeight: "calc(100vh - 260px)" }}
      >
        {applications.length === 0 ? (
          <div
            className="flex items-center justify-center h-20 rounded-xl text-xs"
            style={{
              color: t.textMuted,
              border: `1.5px dashed ${t.border}`,
              background: "transparent",
            }}
            data-testid={`kanban-empty-${stage}`}
          >
            No candidates
          </div>
        ) : (
          applications.map((app) => (
            <KanbanCard
              key={app.id}
              application={app}
              onCardClick={onCardClick}
              isDark={isDark}
              t={t}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── KanbanBoard (main export) ────────────────────────────────────────────────
export default function KanbanBoard({
  stages,
  applicationsByStage,
  onStageChange,
  onCardClick,
  isDark,
  t,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) return;

    const applicationId = active.id;
    const newStage = over.id;

    // Find current stage of this application
    let currentStage = null;
    for (const stage of stages) {
      const apps = applicationsByStage[stage] || [];
      if (apps.find((a) => a.id === applicationId)) {
        currentStage = stage;
        break;
      }
    }

    if (currentStage && newStage !== currentStage) {
      onStageChange(applicationId, newStage);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div
        className="flex gap-4 overflow-x-auto pb-4"
        style={{ paddingBottom: "16px" }}
        data-testid="kanban-board"
      >
        {stages.map((stage) => (
          <KanbanColumn
            key={stage}
            stage={stage}
            applications={applicationsByStage[stage] || []}
            onCardClick={onCardClick}
            onStageChange={onStageChange}
            isDark={isDark}
            t={t}
          />
        ))}
      </div>
    </DndContext>
  );
}
